<script setup>
import PrimaryButton from "../../../../frontend/src/components/ui/PrimaryButton.vue";
import {computed, onMounted, reactive, ref, watch} from "vue";
import useForm from "@/composables/useForm";
import corporatesService from "@/services/corporates.service";
import startupsService from "@/services/startups.service";
import partnershipsService from "@/services/partnerships.service";
import ObjectSelector from "@/components/ui/ObjectSelector.vue";
import marketSubSectorsService from "@/services/market-sub-sectors.service";
import VueMultiselect from "vue-multiselect";
import {useStore} from "vue2-helpers/vuex";

const emit = defineEmits(['saved']);
const store = useStore();

const form = reactive({
    corporateId: null,
    startupId: null,
    subsectorId: null,
    partnershipYear: null,
    summary: null,
    source: null,
})

const corporate = ref(null);
const startup = ref(null);
const subsector = ref(null);

const {populateForm, resetForm, flatErrors, setErrors, clearErrors} = useForm(form);
const showModal = ref(false);
const id = ref(null);
const loading = ref(false);

const canSave = computed(() => form.corporateId && form.startupId);

watch(corporate, (value) => {
    if (value) {
        form.corporateId = value.id;
    } else {
        form.corporateId = null;
    }
});

watch(startup, (value) => {
    if (value) {
        form.startupId = value.id;
    } else {
        form.startupId = null;
    }
});

watch(subsector, (value) => {
    if (value) {
        form.subsectorId = value.id;
    } else {
        form.subsectorId = null;
    }
});

onMounted(() => {
    store.dispatch('fetchSubsectors');
});

async function getData() {
    const {data} = await partnershipsService.getEdit(id.value);
    populateForm(data);

    if (data.corporate_id) {
        corporatesService.getData(data.corporate_id).then(response => {
            corporate.value = response.data;
        });
    }

    if (data.startup_id) {
        startupsService.getData(data.startup_id).then(response => {
            startup.value = response.data;
        });
    }

    if (data.subsector_id) {
        marketSubSectorsService.getData(data.subsector_id).then(response => {
            subsector.value = response.data;
        });
    }
}

async function save() {
    loading.value = true;
    clearErrors();

    try {
        const {data} = await partnershipsService.save(id.value, form);

        if (!Object.keys(data.errors).length) {
            showModal.value = false;
            emit('saved');
        } else {
            setErrors(data.errors);
        }
    } finally {
        loading.value = false;
    }
}

function open(itemId) {
    corporate.value = null;
    startup.value = null;
    subsector.value = null;

    resetForm();
    clearErrors();

    if (itemId) {
        id.value = itemId;
        getData();
    } else {
        id.value = 0;
    }

    showModal.value = true;
}

defineExpose({open});
</script>

<template>
    <b-modal
        v-model="showModal"
        modal-class="modal-w-md" :title="(id ? 'Edit' : 'Add') + ' Partnership'"
    >
        <form class="form pb-5">
            <template v-if="flatErrors.length">
                <div class="form__error" v-for="(error, index) in flatErrors" :key="index">{{error}}</div>
            </template>

            <div class="form__item">
                <h5 class="heading5 heading5--secondary">Corporate *</h5>

                <ObjectSelector
                    v-model="corporate"
                    placeholder="Select Corporate"
                    :get-list-method="(query) => corporatesService.search(query)"
                />
            </div>

            <div class="form__item">
                <h5 class="heading5 heading5--secondary">Startup *</h5>

                <ObjectSelector
                    v-model="startup"
                    placeholder="Select Startup"
                    :get-list-method="(query) => startupsService.search(query)"
                />
            </div>

            <div class="form__item">
                <h5 class="heading5 heading5--secondary">Subsector *</h5>

                <VueMultiselect
                    v-model="subsector"
                    placeholder="Select Subsector"
                    :options="store.getters.subsectorsList"
                    select-label=""
                    deselect-label=""
                    label="name"
                    track-by="name"
                    class="multiselect-mid pl-2"
                />
            </div>

            <div class="form__item">
                <h5 class="heading5 heading5--secondary">Partnership Year</h5>
                <input class="form-control--mid form-control" type="number" v-model="form.partnershipYear">
            </div>

            <div class="form__item">
                <h5 class="heading5 heading5--secondary">Summary</h5>
                <textarea class="form-control--mid form-control" type="text" v-model="form.summary"/>
            </div>

            <div class="form__item">
                <h5 class="heading5 heading5--secondary">Source</h5>
                <input class="form-control--mid form-control" type="text" v-model="form.source">
            </div>
        </form>

        <template #modal-footer>
            <PrimaryButton
                :disabled="!canSave"
                :loading="loading"
                @click="save"
            />
        </template>
    </b-modal>
</template>

<style scoped>

</style>
